import * as React from 'react';
import {useRef, useEffect, useContext} from 'react';
import {
  assignProps,
  createViewConfig,
  createView,
  useTokenValue,
  media,
  AnimatePresenceProvider,
} from '@sail/engine';
import {arrowUpDown} from '@sail/icons/react/Icon';
import type {View} from '@sail/engine';
import type {
  Expandable,
  Filterable,
  ListCollectionComponent,
  MenuTriggerProps,
  MultiSelectState,
  SingleSelectionProps,
  TreeState,
} from '@sail/react-aria';
import {
  FocusScope,
  useListCollection,
  useMultiSelectState,
  useSelect,
} from '@sail/react-aria';
import {FormField, useFormFieldProps} from './FormField';
import {Icon} from './Icon';
import {Button} from './Button';
import {DeprecatedPopoverContext, Popover} from './Popover';
import {ListBox, ListBoxContext, ListBoxConfig} from './ListBox';
import {useFieldContexts} from '../hooks/useFieldContexts';
import type {FormFieldProps} from './FormField';
import type {ButtonProps} from './Button';
import {SearchField, SearchFieldContext} from './SearchField';
import {ListBoxGroupTransforms, ListBoxGroup} from './ListBoxGroup';
import type {ListBoxGroupProps} from './ListBoxGroup';
import {
  ListBoxItemTransforms,
  ListBoxItem,
  ListBoxItemConfig,
} from './ListBoxItem';
import type {ListBoxItemProps} from './ListBoxItem';
import {
  anyCoarsePointerCheck,
  smallScreenCheck,
} from './utils/mobileDeviceCheck';
import {tokens} from '../tokens';
import {SelectFieldConfig} from './SelectFieldConfig';
import {css} from '../css';

const useAutofocus = (state: MultiSelectState & TreeState) => {
  const autoFocusRef = useRef(true);

  useEffect(() => {
    // Reset the autofocus flag when the list closes or opens.
    autoFocusRef.current = true;
  }, [state.isOpen]);

  useEffect(() => {
    // If the list is open and there are items, focus the first selected item.
    // If there's no selected item, leave focus on the list itself instead of
    // focusing the first item to mantain compatibility with the previous behavior.
    if (autoFocusRef.current && state.isOpen && state.collection.size > 0) {
      const manager = state.selectionManager;
      const firstSelectedItem = Array.from(manager.selectedKeys).find((key) =>
        manager.canSelectItem(key),
      );

      if (firstSelectedItem) {
        manager.setFocused(true);
        manager.setFocusedKey(firstSelectedItem);
      }

      autoFocusRef.current = false;
    }
  }, [state]);
};

const TruncatedText = createView('span', {
  css: {
    overflowX: 'hidden',
    overflowY: 'visible',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
});

// Item

export type SelectFieldItemProps = ListBoxItemProps;
export const SelectFieldItemTransforms = ListBoxItemTransforms;

export const SelectFieldItemConfig = createViewConfig({
  name: 'SelectItem',
  props: {} as SelectFieldItemProps,
  flattens: true,
});

export const SelectFieldItem = SelectFieldItemConfig.createView((props) => {
  return <ListBoxItem {...props} />;
});

// Group

export type SelectFieldGroupProps = ListBoxGroupProps;
export const SelectFieldGroupTransforms = ListBoxGroupTransforms;

export const SelectFieldGroupConfig = createViewConfig({
  name: 'SelectGroup',
  props: {} as SelectFieldGroupProps,
  flattens: true,
});

export const SelectFieldGroup = SelectFieldGroupConfig.createView((props) => {
  return <ListBoxGroup {...props} />;
});

// SelectField

export type SelectFieldProps<T> = FormFieldProps<
  ListCollectionComponent<
    T,
    SelectFieldItemProps,
    typeof SelectFieldItemTransforms.defaultField,
    SelectFieldGroupProps,
    typeof SelectFieldGroupTransforms.idField
  > &
    Expandable &
    Filterable<T> &
    Omit<SingleSelectionProps, 'disallowEmptySelection'> &
    MenuTriggerProps & {
      placeholder?: string;
      /** Whether the input is disabled. */
      disabled?: boolean;
      disabledKeys?: Iterable<React.Key>;
      /** Whether the input can be selected but not changed by the user. */
      readOnly?: boolean;
      subviews?: View.Subviews<{
        button: typeof Button;
        value: 'span';
      }>;
      size?: ButtonProps['size'];
      ['data-testid']?: string;
    },
  typeof Button
>;

export const SelectField = SelectFieldConfig.createGenericView(
  <T extends object>({
    data,
    item,
    group,
    children,
    label,
    placeholder,
    subviews,
    filterable,
    defaultFilter,
    size,
    ...props
  }: View.RenderProps<SelectFieldProps<T>>) => {
    const collection = useListCollection({
      children,
      data,
      item,
      group,
      itemTransforms: SelectFieldItemTransforms,
      groupTransforms: SelectFieldGroupTransforms,
    });

    const state = useMultiSelectState({
      getItems: collection.getItems,
      selection: props,
      trigger: props,
      expandable: props,
    });

    const buttonRef = useRef<HTMLAnchorElement>(null);

    const {labelProps, triggerProps, valueProps, listboxProps} = useSelect(
      {isDisabled: props.disabled || props.readOnly || false},
      state,
      buttonRef,
    );

    const [fieldProps] = useFormFieldProps({label, subviews, ...props});

    if (children && filterable) {
      throw new Error(
        'SelectField with the `filterable` prop was provided items as JSX children. This is not supported; ' +
          'to use filtering, provide items with the `data` prop',
      );
    }

    // Autofocus the first item when the list opens. This is a workaround to make autofocus
    // work with our collections API. Our collections are built differently from react-aria's
    // collections. We only add items to the collection when they're rendered while react-aria
    // expects all items to be in the collection before the list is opened. This means that when
    // the list is opened, react-aria has already triggered focus on an item that doesn't exist
    // in the collection and we need to manually trigger focus.
    useAutofocus(state);

    const smallViewport = useTokenValue(tokens.viewport.small);
    const {useDeprecatedPopoverComponents} = useContext(
      DeprecatedPopoverContext,
    );

    const isMobileAndTouch =
      smallScreenCheck(smallViewport) &&
      anyCoarsePointerCheck() &&
      !useDeprecatedPopoverComponents;

    // swap the mobile media query for something that is always false
    const susSafeMediaQueryDown = (
      useDeprecatedPopoverComponents
        ? media.down('0px')
        : media.query(
            `${media.down(tokens.viewport.mobile)}, and (any-pointer: coarse)`,
          )
    ) as '@media query down';

    // always show desktop styles
    const susSafeMediaQueryUp = (
      useDeprecatedPopoverComponents
        ? media.query('all')
        : media.query(
            `${media.up(
              tokens.viewport.mobile,
            )}, not all and (any-pointer: coarse)`,
          )
    ) as '@media query up';

    const listbox = (
      <AnimatePresenceProvider>
        <FocusScope restoreFocus autoFocus={filterable}>
          <ListBoxConfig.Customize
            css={{
              keyline: 'transparent',
              backgroundColor: 'form',
              [susSafeMediaQueryDown]: {
                padding: 'space.150',
              },
            }}
            uses={[
              css({
                [susSafeMediaQueryDown]: {
                  flexGrow: '0',
                },
              }),
            ]}
          >
            <ListBoxItemConfig.Customize
              css={{
                [susSafeMediaQueryDown]: {
                  paddingX: 'small',
                  paddingY: 'space.75',
                },
              }}
            >
              {filterable ? (
                <SearchFieldContext.Provider
                  value={{collection, options: {shouldUsePopover: false}}}
                >
                  <SearchField
                    menuTrigger="focus"
                    hiddenElements={['label']}
                    label={label}
                    data={data}
                    defaultFilter={defaultFilter}
                    size={isMobileAndTouch ? 'large' : undefined}
                    item={item}
                    group={group}
                    expandable={props.expandable}
                    defaultExpandedKeys={props.defaultExpandedKeys}
                    expandedKeys={props.expandedKeys}
                    selectedKey={state.selectedKey}
                    disabledKeys={props.disabledKeys}
                    defaultValue=""
                    shouldCloseOnBlur={false}
                    isOpen // If we are rendering the searchfield, we always want to render the listbox below it
                    onSelectionChange={(key) => {
                      if (key) {
                        state.setSelectedKey(key as string);
                        state.close();
                      }
                    }}
                    onSubmit={(_value, results) => {
                      if (!state.isFocused) {
                        const key = Array.from(results).find(
                          (key) =>
                            state.collection.getItem(key)?.type !== 'section',
                        );

                        if (key) {
                          state.setSelectedKey(key as string);
                          state.close();
                        }
                      }
                    }}
                    css={{
                      borderRadius: 0,
                      backgroundColor: 'form',
                      // match Popover's border radius
                      borderTopLeftRadius: 'medium',
                      borderTopRightRadius: 'medium',
                      [susSafeMediaQueryUp]: {
                        borderBottom: 'solid 1px',
                        borderBottomColor: 'border',
                      },
                      [susSafeMediaQueryDown]: {
                        borderBottom: 'none',
                        maxHeight: 'calc(100svh - 40px)',
                      },
                    }}
                    subviews={{
                      listbox: {
                        css: {
                          borderTopLeftRadius: 0,
                          borderTopRightRadius: 0,
                          [susSafeMediaQueryDown]: {
                            maxHeight: 'calc(100svh - 40px)',
                          },
                        },
                      },
                      inputs: {
                        css: {
                          borderTopLeftRadius: 'xsmall',
                          borderTopRightRadius: 'xsmall',
                          // margin: xsmall is used to provision enough space for the focus ring
                          // in order to visually appear "inset"
                          margin: 'xsmall',
                          [susSafeMediaQueryUp]: {
                            keyline: 'transparent',
                            ':focus-within': {
                              keyline: 'transparent',
                            },
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                          },
                          [susSafeMediaQueryDown]: {
                            borderBottomLeftRadius: 'xsmall',
                            borderBottomRightRadius: 'xsmall',
                            marginX: 'space.250',
                            keyline: 'border',
                            marginTop: 'space.150',
                            marginBottom: 'xsmall',
                            borderBottom: 'none',
                          },
                        },
                      },
                    }}
                  />
                </SearchFieldContext.Provider>
              ) : (
                <ListBoxContext.Provider
                  value={{state, collection, options: listboxProps}}
                >
                  <ListBox
                    label={label}
                    data={data}
                    item={item}
                    group={group}
                  />
                </ListBoxContext.Provider>
              )}
            </ListBoxItemConfig.Customize>
          </ListBoxConfig.Customize>
        </FocusScope>
      </AnimatePresenceProvider>
    );

    const {selectedItem} = state;

    return (
      <FormField
        {...fieldProps}
        subviews={{
          label: {
            uses: [assignProps(labelProps as View.IntrinsicElement<'label'>)],
          },
          inputs: {css: {minWidth: '0'}},
        }}
        css={{minWidth: 240}}
      >
        <Button
          ref={buttonRef}
          {...valueProps}
          data-testid={props['data-testid']}
          css={{
            baselineAlign: 'auto',
            gap: 'space.75',
            // buttons have `width: 'fit'` by default, but fields stretch to fill the available space
            width: 'auto',
            stack: 'x',
            alignY: 'center',
            display: 'block',
            // Appearance
            topShadow: 'form',
            keyline: 'form',
            backgroundColor: 'form',
            textColor: 'form',
            border: 'none',
            borderRadius: 'form',

            // Hovered
            ':hover:not(:active):not([aria-disabled="true"])': {
              topShadow: 'form.hovered',
              keyline: 'form.hovered',
              backgroundColor: 'form.hovered',
              textColor: 'form.hovered',
            },
            // Focus
            ':focus': {
              focusRing: 'focus',
              topShadow: 'form.focused',
              keyline: 'form.focused',
              backgroundColor: 'form.focused',
              textColor: 'form.focused',
            },

            ':active:not([aria-disabled="true"])': {
              topShadow: 'form.pressed',
              keyline: 'form.pressed',
              backgroundColor: 'form.pressed',
              textColor: 'form.pressed',
            },

            // Error styles
            '[aria-invalid="true"]': {
              keyline: 'form.error',
            },

            // Disabled styles
            '[aria-disabled="true"]': {
              color: 'form.disabled',
              backgroundColor: 'form.disabled',
              borderColor: 'form.disabled',
              pointerEvents: 'none',
              topShadow: '0 0 0 0 transparent',
            },
            // turn off "wash" effect over disabled buttons
            '[aria-disabled="true"]::after': {
              display: 'none',
            },
          }}
          size={size}
          type="secondary"
          inherits={subviews.button}
          uses={[
            assignProps((props) => {
              const contextProps = useFieldContexts(props);
              return contextProps;
            }),
            assignProps(triggerProps as ButtonProps),
            assignProps((props) => ({
              // truncation styles can't be applied to flex containers.
              // and wrapping the entire content would break JSX-based item layouts
              children: React.Children.map(props.children, (child) =>
                typeof child === 'string' ? (
                  <TruncatedText> {child}</TruncatedText>
                ) : (
                  child
                ),
              ),
            })),
          ]}
        >
          {selectedItem?.props.icon}
          {selectedItem?.props.children || selectedItem?.textValue}
          {!selectedItem && placeholder}

          <Icon
            icon={arrowUpDown}
            aria-hidden
            size="xsmall"
            css={{marginLeft: 'auto'}}
          />
        </Button>
        {/*
          This is a tradeoff we made when allowing items as renderable
          children. Unfortunately, we *always* need to render the collection
          so the items are always registered. For anything but simple cases,
          we will encourage users to use the `data` pattern but it's still
          worthwhile to support this for simple cases.
         */}
        <AnimatePresenceProvider
          emptyRender={
            children ? <div style={{display: 'none'}}>{listbox}</div> : null
          }
        >
          <Popover
            open={state.isOpen}
            targetRef={buttonRef}
            placement="bottom left"
            shouldCloseOnBlur
            flip={!filterable}
            shift={false}
            size={{
              maxHeight: 'fill',
              matchWidth: true,
            }}
            onClose={() => state.close()}
            css={{
              [susSafeMediaQueryUp]: {
                stack: 'y',
              },
              [susSafeMediaQueryDown]: {
                alignY: 'top',
              },
            }}
            mobileTitle={label || placeholder || props['aria-label']}
          >
            {listbox}
          </Popover>
        </AnimatePresenceProvider>
      </FormField>
    );
  },
);
